import React, { useEffect } from 'react';
import { ResourceStrings } from '../../../ResourceStrings';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/rootState';
import { SearchInfo, OperationState } from '../../../store/appState';
import styled from 'styled-components/macro';
import * as Actions from '../../../store/actions';
import { App } from '../../../app';
import { Activity } from '../../../UserProperties';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import RefreshButton from './RefreshButton';
import Button from '../../shared/Button';
import { Link } from 'react-router-dom';

const ActivityTranslations: { [activity: string]: (date: string) => string } = {
    "commented": ResourceStrings.actCommented,
    "deleted": ResourceStrings.actDeleted,
    "posted": ResourceStrings.actPosted,
    "changed the state of": ResourceStrings.actChangedState,
    "voted": ResourceStrings.actVoted,
    "action": ResourceStrings.actGeneral,
    "solved": ResourceStrings.actSolved,
};

const getActivityInfoText = (item: Activity): string => {
    const translate: (date: string) => string = ActivityTranslations[item.activity] || ResourceStrings.actGeneral;
    return translate(App.ParseDateTimeToLocal(item.date));
}

export interface ActivityListProps {
}

export default function ActivityList(props: ActivityListProps): JSX.Element {
    const state = useSelector((state: RootState) => state.appState);

    const list = state.myActivity.activityResults.activities ?? [];

    useEffect(() => {
        const info: SearchInfo = state.myActivity;
        if (!info.activityResults || !info.activityResults.activities || (info.activityResults.activities.length === 0)) { // if no results present yet
            Actions.loadActivity();
        }
    }, [App.settings, state.myActivity]);

    const refreshClicked = (): void => {
        Actions.loadActivity();
    };

    // @TODO: Find better way to check if all activities have been loaded
    // This is the same logic as existing feedback tool
    // It will incorrectly continue to show load more button if last page contains same number of items as the page size
    const fullPageCountLoaded: number = Math.floor(state.myActivity.activityResults.activities.length / Actions.ACTIVITY_PAGE_SIZE);
    const partialResultCountLoaded: number = state.myActivity.activityResults.activities.length % Actions.ACTIVITY_PAGE_SIZE;

    const loadMoreClicked = (fullPageCountLoaded: number, partialResultCountLoaded: number): void => {
        // const fullPageCountLoaded: number = Math.floor(activityResults.activities.length / Actions.ACTIVITY_PAGE_SIZE);
        // const partialResultCountLoaded: number = activityResults.activities.length % Actions.ACTIVITY_PAGE_SIZE;
        const pagesLoaded: number = partialResultCountLoaded > 0 ? fullPageCountLoaded + 1 : fullPageCountLoaded;
        Actions.loadActivity(pagesLoaded + 1); // added +1 since AnswerHub starts from 1
    }

    const fetchState = state.myActivity.state;

    return <Wrapper>
        <div>
            <RefreshButton
                disabled={fetchState === OperationState.Executing}
                onClick={() => refreshClicked()} />
        </div>

        {fetchState === OperationState.Success && list.length === 0 && <div>{ResourceStrings.noItemsFound()}</div>}

        {!!(fetchState === OperationState.Success || (fetchState === OperationState.Executing && state.myActivity.isLoadingMore))
            && list.map((item, i) => (
                <ActivityRecord key={i}>
                    {/* @TODO: Show activity link on same page instead of going to devcom */}

                    <Link className="title" to={App.getTicketPath(item.rootPostId, item.rootTitle)}>{App.StripAllHtml(item.rootTitle)}</Link>

                    <div className="info">{getActivityInfoText(item)}</div>
                </ActivityRecord>
            ))
        }

        {fetchState === OperationState.Success && !state.myActivity.loadedEverything && (
            <LoadMore>
                <Button
                    data-cy="rap-load-more-btn"
                    buttonType="link"
                    onClick={() => loadMoreClicked(fullPageCountLoaded, partialResultCountLoaded)}>
                    {ResourceStrings.loadMore()}
                </Button>
            </LoadMore>
        )}

        {fetchState === OperationState.Executing && (
            <Progress>
                {ResourceStrings.loadingMyItems()}
                <Spinner size={SpinnerSize.large} />
            </Progress>
        )}

        {fetchState === OperationState.Failed && <div>
            {ResourceStrings.errorLoadingMyItems(state.myActivity.errorMessage)}
        </div>}
    </Wrapper>
}

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;

    >* + * {
        margin-top: 1.5rem;
    }
`;

const LoadMore = styled.div`
    text-align: center;
    margin: 0.8rem 0;

    button {
        font-size: 1.4rem;
    }
`;

const ActivityRecord = styled.article`
    .info {
        color: var(--color-on-background-lighter);
    }
`;

const Progress = styled.div`
    text-align: center;
    margin-top: 10rem;

    .ms-Spinner {
        margin-top: 2rem;
    }
`;
